import { zodResolver } from '@hookform/resolvers/zod';
import {
  Attachment,
  CreateTicketRequest,
  CreateTicketWithTokenRequest,
  type User,
  type ZammadClient,
} from '@kalos/kalos-rpc';
import {
  Button,
  FileUploader,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@kalos/ui';
import { CheckCircle2 } from 'lucide-react';
import { type FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  CATEGORY_TO_GROUP_ID,
  DEFAULT_PRIORITY_ID,
  DEFAULT_PRIORITY_NAME,
  GROUP_ID_TO_NAME,
  TICKET_CATEGORIES,
  type TicketCategory,
} from './constants';

const ticketFormSchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters'),
  subject: z.string().min(5, 'Subject must be at least 5 characters'),
  body: z.string().min(10, 'Description must be at least 10 characters'),
  category: z.enum([
    TICKET_CATEGORIES.HARDWARE,
    TICKET_CATEGORIES.WEB_APP,
    TICKET_CATEGORIES.MOBILE_APP,
    TICKET_CATEGORIES.FEATURE_REQUEST,
  ]),
  files: z.array(z.instanceof(File)).optional(),
});

type TicketFormValues = z.infer<typeof ticketFormSchema>;

export interface ZammadHelpDeskFormProps {
  /**
   * Zammad client instance for API calls
   */
  zammadClient: ZammadClient;
  /**
   * User submitting the ticket (optional - only for authenticated dashboard users)
   */
  user?: User;
  /**
   * Token for mobile app authentication
   * When provided, uses CreateTicketWithToken instead of CreateTicket
   */
  token?: string;
  /**
   * Callback when ticket is successfully created
   */
  onSuccess?: (ticketNumber: string) => void;
  /**
   * Default category to pre-select
   */
  defaultCategory?: TicketCategory;
  /**
   * Additional tags to add to the ticket
   */
  additionalTags?: string[];
  /**
   * Disable the form submission
   */
  disabled?: boolean;
}

export const ZammadHelpDeskForm: FC<ZammadHelpDeskFormProps> = ({
  zammadClient,
  user,
  token,
  onSuccess,
  defaultCategory = TICKET_CATEGORIES.WEB_APP,
  additionalTags = [],
  disabled = false,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedTicketNumber, setSubmittedTicketNumber] = useState<string | null>(null);

  const form = useForm<TicketFormValues>({
    resolver: zodResolver(ticketFormSchema),
    defaultValues: {
      title: '',
      subject: '',
      body: '',
      category: defaultCategory,
      files: [],
    },
  });

  const onSubmit = async (data: TicketFormValues) => {
    setIsSubmitting(true);
    try {
      // Map category to group ID
      const groupId = CATEGORY_TO_GROUP_ID[data.category];
      const groupName = GROUP_ID_TO_NAME[groupId];

      // Convert files to attachments
      const attachments: Attachment[] = [];
      if (data.files && data.files.length > 0) {
        for (const file of data.files) {
          const arrayBuffer = await file.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);

          attachments.push(
            Attachment.create({
              filename: file.name,
              content: uint8Array,
              mimeType: file.type || 'application/octet-stream',
            }),
          );
        }
      }

      // Build customer email
      const customerEmail =
        user?.email || (user?.id ? `user${user.id}@kalos.com` : 'mobile-user@kalos.com');

      // Build note with user info
      let note = '';
      if (user?.id) {
        note = `Submitted by: ${user.firstname} ${user.lastname} (ID: ${user.id})\nCategory: ${data.category}`;
      } else {
        note = `Submitted via Mobile App\nCategory: ${data.category}`;
      }

      // Build tags
      const tags = ['kalos-dashboard', data.category, ...additionalTags];
      if (token) {
        tags.push('mobile-app', 'token-auth');
      }

      const request = CreateTicketRequest.create({
        title: data.title,
        subject: data.subject,
        body: data.body,
        bodyContentType: 'text/plain',
        priority: DEFAULT_PRIORITY_NAME,
        priorityId: DEFAULT_PRIORITY_ID,
        group: groupName,
        groupId: groupId,
        state: 'new',
        stateId: 1, // New state
        customerEmail: customerEmail,
        attachments: attachments,
        tags: tags,
        note: note,
        articleInternal: false,
      });

      let response;

      // Use CreateTicketWithToken if token is provided (mobile app)
      // Otherwise use regular CreateTicket (authenticated dashboard users)
      if (token) {
        // Build request for CreateTicketWithToken (has different structure)
        const tokenRequest = CreateTicketWithTokenRequest.create({
          token: token,
          title: data.title,
          subject: data.subject,
          body: data.body,
          bodyContentType: 'text/plain',
          group: groupName,
          groupId: groupId,
          priority: DEFAULT_PRIORITY_NAME,
          priorityId: DEFAULT_PRIORITY_ID,
          attachments: attachments,
          tags: tags,
        });

        response = await zammadClient.createTicketWithToken(tokenRequest);
      } else {
        response = await zammadClient.createTicket(request);
      }

      if (response.success) {
        setSubmittedTicketNumber(response.ticketNumber);
        onSuccess?.(response.ticketNumber);
      } else {
        throw new Error(response.message || 'Failed to create ticket');
      }
    } catch (error) {
      console.error('Error creating ticket:', error);

      // Check if error is authentication-related
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const isAuthError =
        errorMessage.toLowerCase().includes('auth') ||
        errorMessage.toLowerCase().includes('token') ||
        errorMessage.toLowerCase().includes('unauthorized');

      toast({
        title: isAuthError ? 'Authentication Error' : 'Error',
        description: isAuthError
          ? 'Your session has expired. Please return to the mobile app and try again.'
          : 'There was an error creating your support ticket. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitNew = () => {
    form.reset();
    form.setValue('category', defaultCategory);
    setSubmittedTicketNumber(null);
  };

  if (submittedTicketNumber) {
    return (
      <div className="flex flex-col items-center gap-4 py-12 text-center">
        <CheckCircle2 className="size-16 text-green-600" />
        <div className="space-y-1">
          <h2 className="text-xl font-semibold">Ticket Submitted</h2>
          <p className="text-muted-foreground">
            Your support ticket #{submittedTicketNumber} has been created successfully.
          </p>
        </div>
        <Button type="button" onClick={handleSubmitNew}>
          Submit New Ticket
        </Button>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Category</FormLabel>
              <Select onValueChange={field.onChange} value={field.value} disabled={isSubmitting}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={TICKET_CATEGORIES.HARDWARE}>
                    {TICKET_CATEGORIES.HARDWARE}
                  </SelectItem>
                  <SelectItem value={TICKET_CATEGORIES.WEB_APP}>
                    {TICKET_CATEGORIES.WEB_APP}
                  </SelectItem>
                  <SelectItem value={TICKET_CATEGORIES.MOBILE_APP}>
                    {TICKET_CATEGORIES.MOBILE_APP}
                  </SelectItem>
                  <SelectItem value={TICKET_CATEGORIES.FEATURE_REQUEST}>
                    {TICKET_CATEGORIES.FEATURE_REQUEST}
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>Select the type of issue or request</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input
                  placeholder="Brief title for your ticket"
                  {...field}
                  disabled={isSubmitting}
                />
              </FormControl>
              <FormDescription>A short, descriptive title for your support request</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="subject"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Subject</FormLabel>
              <FormControl>
                <Input
                  placeholder="What is this ticket about?"
                  {...field}
                  disabled={isSubmitting}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="body"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Please provide detailed information about your issue or request..."
                  className="min-h-[200px]"
                  {...field}
                  disabled={isSubmitting}
                />
              </FormControl>
              <FormDescription>
                Include as much detail as possible to help us assist you better
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="files"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Attachments (Optional)</FormLabel>
              <FormControl>
                <FileUploader
                  value={field.value}
                  onValueChange={field.onChange}
                  multiple
                  maxFiles={Infinity}
                  maxSize={1024 * 1024 * 10}
                  accept={{
                    'image/*': [],
                    'application/pdf': ['.pdf'],
                    'application/msword': ['.doc'],
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
                      '.docx',
                    ],
                    'text/plain': ['.txt', '.log'],
                  }}
                  disabled={isSubmitting}
                />
              </FormControl>
              <FormDescription>
                Attach screenshots, logs, or other relevant files (max 10MB per file)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex gap-4">
          <Button type="submit" isLoading={isSubmitting} disabled={isSubmitting || disabled}>
            Submit Ticket
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              form.reset();
              form.setValue('category', defaultCategory);
            }}
            disabled={isSubmitting}
          >
            Clear Form
          </Button>
        </div>
      </form>
    </Form>
  );
};
